import { useMemo, useState } from "react";

import { MetricBlock } from "../api";
import PlayerCell from "../components/PlayerCell";
import ProgressBar from "../components/ProgressBar";
import SpellIcon from "../components/SpellIcon";
import { classFor } from "../data/classes";

interface DeathEvent {
  timestamp?: number;
  fightId?: number;
  fightName?: string;
  killingBlow?: string;
  killingBlowIcon?: string;
  overkill?: number;
}

interface Row {
  name?: string;
  type?: string;
  icon?: string;
  total: number;
  perSec?: number;
  deaths?: number;
  deathEvents?: DeathEvent[];
}

const fmt = (n: number) => n.toLocaleString("en-US");

type Tab = "deaths" | "damage";

export default function TotalDamageTaken({ block }: { block: MetricBlock }) {
  const rows = (block.rows as unknown as Row[]) ?? [];
  const raidTotal =
    (block.totals?.raidTotal as number | undefined) ??
    rows.reduce((a, r) => a + r.total, 0);
  const raidDeaths =
    (block.totals?.raidDeaths as number | undefined) ??
    rows.reduce((a, r) => a + (r.deaths ?? 0), 0);

  const [selected, setSelected] = useState<Tab>("deaths");

  if (!rows.length) {
    return <p className="text-sm text-gray-500">No damage taken.</p>;
  }

  return (
    <div className="space-y-3">
      <Tabs
        selected={selected}
        onSelect={setSelected}
        deathCount={raidDeaths}
        damageTotal={raidTotal}
      />
      {selected === "deaths" ? (
        <DeathsTable rows={rows} totalDeaths={raidDeaths} />
      ) : (
        <DamageTable rows={rows} raidTotal={raidTotal} />
      )}
    </div>
  );
}

/* ----------------------------- Tabs ----------------------------- */

function Tabs({
  selected,
  onSelect,
  deathCount,
  damageTotal,
}: {
  selected: Tab;
  onSelect: (t: Tab) => void;
  deathCount: number;
  damageTotal: number;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      <Tab
        active={selected === "deaths"}
        onClick={() => onSelect("deaths")}
        label="Deaths"
        aside={`${deathCount}`}
      />
      <Tab
        active={selected === "damage"}
        onClick={() => onSelect("damage")}
        label="Damage Taken"
        aside={fmtCompact(damageTotal)}
      />
    </div>
  );
}

function Tab({
  active,
  onClick,
  label,
  aside,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  aside?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium",
        "transition-colors",
        active
          ? "border-accent/40 bg-accent/15 text-accent"
          : "border-border/60 bg-bg/40 text-gray-300 hover:bg-white/5 hover:text-gray-100",
      ].join(" ")}
    >
      <span>{label}</span>
      {aside && (
        <span className="text-[10px] tabular-nums text-gray-500">{aside}</span>
      )}
    </button>
  );
}

const fmtCompact = (n: number) =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(2)}M`
    : n >= 1_000
      ? `${(n / 1_000).toFixed(1)}k`
      : `${Math.round(n)}`;

/* ----------------------------- Deaths ----------------------------- */

function DeathsTable({
  rows,
  totalDeaths,
}: {
  rows: Row[];
  totalDeaths: number;
}) {
  const dead = useMemo(
    () =>
      rows
        .filter((r) => (r.deaths ?? 0) > 0)
        .sort((a, b) => (b.deaths ?? 0) - (a.deaths ?? 0)),
    [rows],
  );

  if (!dead.length) {
    return (
      <section className="rounded border border-border/60 bg-bg/40 px-4 py-3">
        <p className="text-sm text-gray-500">No deaths. gg.</p>
      </section>
    );
  }

  const top = dead[0]?.deaths ?? 0;
  const rankPad = String(dead.length).length;

  return (
    <section className="rounded border border-border/60 bg-bg/40">
      <header className="flex items-baseline justify-between gap-2 px-4 pt-3 pb-2">
        <h3 className="text-sm font-semibold">Deaths</h3>
        <div className="text-xs text-gray-500">
          <span className="text-red-300 font-medium tabular-nums">
            {totalDeaths}
          </span>{" "}
          raid death{totalDeaths === 1 ? "" : "s"}
        </div>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500">
              <th className="pb-2 pl-4 pr-3 font-medium">#</th>
              <th className="pb-2 pr-3 font-medium">Player</th>
              <th className="pb-2 pr-3 font-medium text-right">Deaths</th>
              <th className="pb-2 pr-3 font-medium w-1/4">Relative</th>
              <th className="pb-2 pr-4 font-medium">Killing blows</th>
            </tr>
          </thead>
          <tbody>
            {dead.map((r, i) => {
              const n = r.deaths ?? 0;
              const pct = top ? (n / top) * 100 : 0;
              const cls = classFor(r.type);
              const padded = String(i + 1).padStart(rankPad, "0");
              return (
                <tr
                  key={`${r.name}-${i}`}
                  className="border-t border-border/40 hover:bg-white/[0.03] transition-colors align-top"
                >
                  <td className="py-2 pl-4 pr-3 text-gray-500 tabular-nums">
                    {padded}
                  </td>
                  <td className="py-2 pr-3 max-w-[14rem]">
                    <PlayerCell name={r.name} type={r.type} icon={r.icon} />
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums font-medium text-red-300">
                    {n}
                  </td>
                  <td className="py-2 pr-3">
                    <ProgressBar pct={pct} color={cls.color} />
                  </td>
                  <td className="py-2 pr-4">
                    <DeathEventList events={r.deathEvents ?? []} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function DeathEventList({ events }: { events: DeathEvent[] }) {
  if (!events.length) return <span className="text-gray-700">-</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {events.map((e, i) => (
        <DeathEventChip key={i} event={e} />
      ))}
    </div>
  );
}

function DeathEventChip({ event }: { event: DeathEvent }) {
  const blow = event.killingBlow ?? "?";
  const fight = event.fightName ?? "";
  const title =
    fight ? `${blow} (${fight})` : blow;
  return (
    <span
      className="inline-flex items-center gap-1 rounded border border-border/60 bg-bg/40 px-1.5 py-0.5 text-[11px] text-gray-200 max-w-[16rem]"
      title={title}
    >
      <SpellIcon icon={event.killingBlowIcon} alt={blow} size={14} />
      <span className="truncate">{blow}</span>
      {fight && (
        <span className="text-[10px] text-gray-500 truncate">· {fight}</span>
      )}
    </span>
  );
}

/* ----------------------------- Damage taken ----------------------------- */

function DamageTable({
  rows,
  raidTotal,
}: {
  rows: Row[];
  raidTotal: number;
}) {
  const sorted = useMemo(
    () => [...rows].sort((a, b) => b.total - a.total),
    [rows],
  );
  const top = sorted[0]?.total ?? 0;
  const rankPad = String(sorted.length).length;

  return (
    <section className="rounded border border-border/60 bg-bg/40">
      <header className="flex items-baseline justify-between gap-2 px-4 pt-3 pb-2">
        <h3 className="text-sm font-semibold">Damage Taken</h3>
        <div className="text-xs text-gray-500 tabular-nums">
          <span className="text-gray-300 font-medium">{fmt(raidTotal)}</span>
          {" total"}
        </div>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500">
              <th className="pb-2 pl-4 pr-3 font-medium">#</th>
              <th className="pb-2 pr-3 font-medium">Player</th>
              <th className="pb-2 pr-3 font-medium text-right">Taken</th>
              <th className="pb-2 pr-3 font-medium text-right">DTPS</th>
              <th className="pb-2 pr-4 font-medium w-1/4">% of top</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => {
              const pct = top ? (r.total / top) * 100 : 0;
              const cls = classFor(r.type);
              const padded = String(i + 1).padStart(rankPad, "0");
              return (
                <tr
                  key={`${r.name}-${i}`}
                  className="border-t border-border/40 hover:bg-white/[0.03] transition-colors"
                >
                  <td className="py-1.5 pl-4 pr-3 text-gray-500 tabular-nums">
                    {padded}
                  </td>
                  <td className="py-1.5 pr-3 max-w-[16rem]">
                    <PlayerCell name={r.name} type={r.type} icon={r.icon} />
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums font-medium">
                    {fmt(r.total)}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-gray-400">
                    {r.perSec ? fmt(Math.round(r.perSec)) : "-"}
                  </td>
                  <td className="py-1.5 pr-4">
                    <ProgressBar pct={pct} color={cls.color} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
